import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { Field, Form, Formik, FormikProps } from 'formik';
import { selectors } from '../LoyaltyPage/reducer';
import { selectors as selectorsLogin } from '../LoginPage/reducer';
import { selectors as usersSelectors } from '../../components/ActivePharmacistBlock/reducer';
import * as actions from './actions';
import * as usersActions from '../../components/ActivePharmacistBlock/actions';
import Loader from '../../components/Loader';
import * as Styled from './LoyaltyPage.styles';

import ApplyBlock from './components/ApplyBlock';
import ChosenReceiptBlock from './components/ChosenReceiptBlock';
import { distributeClientBonus } from './helpers';
import AvailableReceiptsBlock from './components/AvailableReceiptsBlock';
import { useBarcodeScanner } from './hooks/useBarcodeScanner';
import qrScan from '../../global/media/qr-scan.png';

const LoyaltyPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const dataUser = useSelector(selectorsLogin.userData);
  const activeUsers = useSelector(usersSelectors.activeUsersData);
  const currentReceipt = useSelector(selectors.currentReceipt);
  const currentReceiptLoading = useSelector(selectors.currentReceiptLoading);
  const currentReceiptError = useSelector(selectors.currentReceiptError);
  const chosenReceipt = useSelector(selectors.chosenReceipt);
  const clientInfo = useSelector(selectors.clientInfo);
  const clientInfoLoading = useSelector(selectors.clientInfoLoading);
  const submitOrderResult = useSelector(selectors.submitOrderResult);

  const [clientPhone, setClientPhone] = useState<string>('+77');
  const [phoneError, setPhoneError] = useState<boolean>(false);
  const [scanMode, setScanMode] = useState<boolean>(false);
  const [currentPharmacist, setCurrentPharmacist] = useState<null | any>(null);
  const [showWithBonuses, setShowWithBonuses] = useState<boolean>(true);

  const totalPrice = Array.isArray(chosenReceipt?.items)
    ? (chosenReceipt?.items || []).reduce(
        (acc: number, item: any) => acc + Number(item?.total_price ?? 0),
        0
      )
    : 0;

  const currentItems = distributeClientBonus(
    chosenReceipt?.items ?? [],
    showWithBonuses ? clientInfo?.balance ?? 0 : 0
  );

  const totalBonuses = (bonuses: 'bonusesToClient' | 'bonusesToPharmacist') =>
    Math.floor(
      currentItems?.reduce((acc, item) => acc + item[bonuses], 0) || 0
    );

  const [finalPrice, setFinalPrice] = useState<number>(0);

  const getReceiptInfo = () => {
    dispatch(actions.getCurrentReceipt.request());
  };

  const getClientBalance = (phone: string) => {
    dispatch(actions.getClientInfo.request({ phone }));
  };

  const onScan = useCallback((text: string) => {
    const m = text.match(/[?&](phone|tel)=([^&]+)/i);
    const raw = m ? decodeURIComponent(m[2]) : text;
    if (raw.match(/^\+77\d{0,9}$/)) {
      setClientPhone(raw);
    } else {
      setPhoneError(true);
    }
    setScanMode(false);
  }, []);

  useBarcodeScanner({
    enabled: scanMode,
    onScan,
    endKeys: ['Enter', 'Tab', 'NumpadEnter'], // слушатель пика у сканера
  });

  useEffect(() => {
    const balanceValue = (clientInfo && clientInfo.balance) ?? 0;
    const currentItems = distributeClientBonus(
      chosenReceipt?.items ?? [],
      showWithBonuses ? balanceValue : 0
    );
    const computed =
      Number(totalPrice) -
      Number(currentItems.reduce((acc, item) => acc + item.appliedBonus, 0));
    setFinalPrice(computed > 0 ? computed : 0);
  }, [clientInfo, totalPrice, showWithBonuses]);

  useEffect(() => {
    if (dataUser && clientInfo) {
      dispatch(
        usersActions.getAllUsers.request({ sourceCode: dataUser.sourceCode })
      );
      getReceiptInfo();
    }
  }, [dataUser, clientInfo]);

  useEffect(() => {
    if (!dataUser || !clientInfo) {
      return;
    }
    const intervalId = setInterval(() => {
      getReceiptInfo();
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [dataUser, clientInfo]);

  useEffect(() => {
    if (!clientPhone) {
      return;
    }
    setPhoneError(false);
    const digitsOnly = clientPhone.replace(/\D/g, '');
    if (digitsOnly.length >= 11) {
      getClientBalance(digitsOnly);
    } else {
      dispatch(actions.resetClientInfo());
      dispatch(actions.resetCurrentReceipt());
    }
  }, [clientPhone]);

  useEffect(() => {
    if (activeUsers) {
      setCurrentPharmacist(
        activeUsers?.find((pharmacist) => pharmacist.phone === dataUser.phone)
      );
    }
  }, [activeUsers]);

  useEffect(() => {
    if ((!chosenReceipt?.items.length && submitOrderResult) || scanMode) {
      setClientPhone('+77');
      setPhoneError(false);
      setShowWithBonuses(true);
      dispatch(actions.setChosenReceipt.request(null));
      dispatch(actions.resetClientInfo());
      dispatch(actions.resetCurrentReceipt());
    }
  }, [chosenReceipt, scanMode]);

  const handlePhoneChange = (
    props: FormikProps<any>,
    event: ChangeEvent<HTMLInputElement>,
    inputName: string
  ) => {
    // @ts-ignore
    const phoneInputValue = event.target.value.replaceAll(/[\s-()]/g, '');
    if (!phoneInputValue.match(/^\+77\d{0,9}$/)) {
      return;
    }
    props.setErrors({
      ...props.errors,
      [inputName]: '',
    });
    props.setFieldValue(inputName, phoneInputValue);
    setClientPhone(phoneInputValue);
  };

  return (
    <Styled.LoyaltyPageContainer>
      {!currentReceipt && currentReceiptLoading && <Loader />}
      <Styled.PharmacyInnerInfo>
        <div>
          {t('LoyaltyPage.PHARMACY')}
          {dataUser?.networkName}
        </div>
        <div className="client-info">
          {t('LoyaltyPage.CLIENT')}
          {process.env.REACT_APP_LOYALTY_BARCODE_ACTIVE === 'true' && (
            <div
              onClick={() => setScanMode((mode) => !mode)}
              style={{ cursor: 'pointer' }}
            >
              {scanMode ? (
                <CircularProgress size={15} />
              ) : (
                <img width={30} height={30} src={qrScan} alt="qr scan icon" />
              )}
            </div>
          )}
          <Formik
            initialValues={{
              phone: clientPhone,
            }}
            enableReinitialize
            onSubmit={() => {}}
          >
            {(props: FormikProps<any>) => (
              <Form className="form-container">
                <div className="form-block">
                  <Field
                    id="phone"
                    name="phone"
                    type="phone"
                    placeholder="Номер телефона клиента"
                    className={`input ${props.errors.phone && 'input-error'}`}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handlePhoneChange(props, e, 'phone')
                    }
                  />
                  {phoneError && (
                    <div className="error-phone-label">
                      {t('LoginPage.PHONE_INCORRECT_PREFIX')}
                    </div>
                  )}
                </div>
              </Form>
            )}
          </Formik>
          {clientInfoLoading && <CircularProgress size={15} />}
          {!clientInfoLoading && clientInfo && (
            <Styled.Balance user>
              {('balance' in clientInfo ? clientInfo.balance : '') ?? ''} Б
            </Styled.Balance>
          )}
        </div>
      </Styled.PharmacyInnerInfo>
      <Styled.ActiveReceipt>
        {currentReceiptError && (
          <div className="active-receipt-error">
            {t('LoyaltyPage.RECEIPT_ERROR')}
          </div>
        )}
        {chosenReceipt ? (
          <ChosenReceiptBlock
            showWithBonuses={showWithBonuses}
            setShowWithBonuses={setShowWithBonuses}
          />
        ) : (
          <AvailableReceiptsBlock />
        )}
        {totalPrice > 0 && chosenReceipt && (
          <Styled.PriceBlock marginTop>
            <Styled.Price>
              {t('LoyaltyPage.BONUSES_WILL_BE_ADDED')}:{' '}
              {totalBonuses('bonusesToClient')} Б
            </Styled.Price>
            <Styled.Price>
              {t('LoyaltyPage.TOTAL')}: {totalPrice} тг
            </Styled.Price>
          </Styled.PriceBlock>
        )}
        {currentReceipt && showWithBonuses && chosenReceipt && (
          <Styled.PriceBlock>
            <Styled.Price green>
              {t('LoyaltyPage.TOTAL_AFTER_DISCOUNT')}: {finalPrice} тг
            </Styled.Price>
          </Styled.PriceBlock>
        )}
      </Styled.ActiveReceipt>
      {chosenReceipt && <ApplyBlock setShowWithBonuses={setShowWithBonuses} />}
      <Styled.Footer>
        <span>{t('LoyaltyPage.PHARMACIST')}</span>
        {dataUser?.phone} {currentPharmacist?.name ?? ''}
        {chosenReceipt && (
          <Styled.Balance>
            {totalBonuses('bonusesToPharmacist') ?? ''} Б
          </Styled.Balance>
        )}
      </Styled.Footer>
    </Styled.LoyaltyPageContainer>
  );
};

export default LoyaltyPage;
